import type { CSSProperties, ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Emoji } from './Emoji';
import { cn, TEAM_COLOR, TEAM_TINT, type Team } from './util';

export type ItemCardState = 'normal' | 'table' | 'sold' | 'disabled';
export type ItemCardSize = 'xs' | 'sm' | 'md' | 'lg';
export type Tier = 'S' | 'A' | 'B' | 'C';

export interface ItemCardProps {
  emoji: string;
  name: string;
  tier?: Tier;
  /** Tiers are secret during play; show only in results / admin views. */
  showTier?: boolean;
  /** normal · table (highlighted: it's on the table) · sold (owner colour) · disabled. */
  state?: ItemCardState;
  /** Owner team for sold cards. */
  owner?: Team;
  /** Sale price (shown on sold cards). */
  price?: number;
  /** Face-down "?" card. Toggle to `false` to flip it over (surprise mode reveal). */
  faceDown?: boolean;
  /** Queue position badge ("Sıradaki" fixed order). */
  index?: number;
  size?: ItemCardSize;
  /** Below `lg`, fill the parent's width instead of the fixed size width (phone Vitrin grid). */
  fluid?: boolean;
  /** Makes the card a button (opener-picks Vitrin). */
  onClick?: () => void;
  /** Extra content under the name (e.g. "EN YÜKSEK: 6"). */
  footer?: ReactNode;
  /** Tilt in degrees. Defaults: lg −1.5°, others 0. */
  tilt?: number;
  className?: string;
  style?: CSSProperties;
  /** Accessible label override. */
  label?: string;
}

const SIZE: Record<ItemCardSize, { box: string; emoji: number; name: string; back: string }> = {
  xs: {
    box: 'w-[84px] min-h-[80px] px-1 pt-2 pb-1.5 border-[2.5px] gap-1',
    emoji: 28,
    name: 'text-[14px]',
    back: 'text-[40px]',
  },
  sm: {
    box: 'w-[124px] min-h-[120px] px-2 pt-3 pb-2 border-3 gap-1.5',
    emoji: 44,
    name: 'text-[18px]',
    back: 'text-[56px]',
  },
  md: {
    box: 'w-[176px] min-h-[170px] px-3 pt-4 pb-3 border-3 gap-2',
    emoji: 66,
    name: 'text-[25px]',
    back: 'text-[84px]',
  },
  lg: {
    box: 'w-[270px] min-h-[250px] px-4 pt-6 pb-4 border-4 gap-3',
    emoji: 112,
    name: 'text-[40px]',
    back: 'text-[130px]',
  },
};

/** Letters in the longest word of `name` (at least 1). */
function longestWord(name: string): number {
  return Math.max(1, ...name.split(/\s+/).map((w) => [...w].length));
}

const TIER_CLS: Record<Tier, string> = {
  S: 'bg-ink text-yellow',
  A: 'bg-team-a text-white',
  B: 'bg-jury text-white',
  C: 'bg-white text-ink',
};

/**
 * Item card: Twemoji + Bangers name. Variants for the Vitrin (xs/sm), the table hero (lg),
 * sold cards in the owner's colour and face-down surprise cards with a 3D flip reveal.
 */
export function ItemCard({
  emoji,
  name,
  tier,
  showTier,
  state = 'normal',
  owner,
  price,
  faceDown = false,
  index,
  size = 'md',
  fluid,
  onClick,
  footer,
  tilt,
  className,
  style,
  label,
}: ItemCardProps) {
  const reduce = useReducedMotion();
  const s = SIZE[size];
  const sold = state === 'sold';
  const onTable = state === 'table';
  const rotation = tilt ?? (size === 'lg' ? -1.5 : 0);
  const shadow =
    size === 'lg'
      ? 'shadow-ink-lg'
      : onTable
        ? 'shadow-ink'
        : size === 'xs'
          ? 'shadow-ink-xs'
          : 'shadow-ink-sm';

  const faceBg: CSSProperties =
    sold && owner
      ? { backgroundColor: TEAM_TINT[owner] }
      : onTable
        ? { backgroundColor: 'var(--color-yellow)' }
        : {};

  const front = (
    <div
      className={cn(
        'relative flex flex-col items-center justify-center border-ink bg-white text-center',
        s.box,
        shadow,
        fluid && 'max-lg:@container max-lg:w-full',
        state === 'disabled' && 'opacity-45 grayscale',
      )}
      style={faceBg}
    >
      {index != null && (
        <span className="comic absolute -top-2.5 -left-2.5 flex size-7 items-center justify-center rounded-full border-2 border-ink bg-white text-[15px] shadow-ink-xs">
          {index}
        </span>
      )}
      {showTier && tier && (
        <span
          className={cn(
            'comic absolute top-1 left-1 border-2 border-ink px-1 text-[12px] leading-tight',
            TIER_CLS[tier],
          )}
        >
          {tier}
        </span>
      )}
      <Emoji
        emoji={emoji}
        label=""
        size={s.emoji}
        className="drop-shadow-[2px_2px_0_rgb(17_17_17/0.18)]"
      />
      <span
        className={cn(
          'comic block w-full leading-[0.95] break-words hyphens-auto',
          s.name,
          // Fluid cards can get narrow: shrink the font so the longest word fits on one line
          // (Bangers is ~0.5em per letter), down to 9px before it breaks mid-word.
          fluid && 'max-lg:text-[length:clamp(9px,calc(100cqi/var(--fit)),14px)]',
        )}
        style={fluid ? { ['--fit' as string]: longestWord(name) * 0.5 } : undefined}
        lang="tr"
      >
        {name}
      </span>
      {footer}
      {sold && owner && (
        <>
          <span
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-1.5 border-t-2 border-ink"
            style={{ backgroundColor: TEAM_COLOR[owner] }}
          />
          {price != null && (
            <span
              className={cn(
                'comic absolute -top-2 -right-2 flex items-center justify-center border-2 border-ink text-white shadow-ink-xs',
                size === 'xs' ? 'min-w-6 px-1 text-[14px]' : 'min-w-8 px-1.5 text-[18px]',
              )}
              style={{ backgroundColor: TEAM_COLOR[owner], rotate: '8deg' }}
            >
              <span className="ink-text pt-[0.08em]">{price}</span>
            </span>
          )}
        </>
      )}
    </div>
  );

  const back = (
    <div
      aria-hidden
      className={cn(
        'absolute inset-0 flex items-center justify-center border-ink bg-ink text-yellow halftone-light [backface-visibility:hidden]',
        s.box,
        shadow,
        fluid && 'max-lg:w-full',
      )}
      style={{ transform: 'rotateY(180deg)' }}
    >
      <span className="absolute inset-1.5 border-2 border-yellow/70" />
      <span className={cn('comic ink-text pt-2', s.back)}>?</span>
    </div>
  );

  const flipping = (
    <motion.div
      className="relative [transform-style:preserve-3d]"
      initial={false}
      animate={{ rotateY: faceDown ? 180 : 0 }}
      transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 170, damping: 17 }}
    >
      {/* Face-down cards are secret (surprise mode): keep the front out of the a11y tree too. */}
      <div className="[backface-visibility:hidden]" aria-hidden={faceDown || undefined}>
        {front}
      </div>
      {back}
    </motion.div>
  );

  const a11yLabel =
    label ??
    (faceDown ? 'Kapalı kart' : sold && price != null ? `${name}, ${price} puana satıldı` : name);
  const wrapperCls = cn(
    'relative inline-block shrink-0 [perspective:900px]',
    fluid && 'max-lg:block max-lg:w-full',
    className,
  );
  const wrapperStyle: CSSProperties = { rotate: `${rotation}deg`, ...style };
  // Motion components take numeric rotate (degrees) so hover/tap rotations interpolate cleanly.
  const motionStyle = { ...style, rotate: rotation };

  if (onClick) {
    return (
      <motion.button
        type="button"
        aria-label={a11yLabel}
        onClick={onClick}
        disabled={state === 'disabled'}
        whileHover={reduce ? undefined : { y: -4, rotate: -1.5 }}
        whileTap={{ scale: 0.94 }}
        transition={{ type: 'spring', stiffness: 600, damping: 20 }}
        className={cn(wrapperCls, 'text-left')}
        style={motionStyle}
      >
        {flipping}
      </motion.button>
    );
  }
  return (
    <div role="group" aria-label={a11yLabel} className={wrapperCls} style={wrapperStyle}>
      {flipping}
    </div>
  );
}
